import { SEO } from '../components/SEO';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';

export const Terms = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="terms-page">
      <SEO 
        title="Terms of Service" 
        description="Surfside Patrol Terms of Service. Review the terms and conditions governing the use of our security services and website."
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Shield className="h-8 w-8 text-[#F5C518] mr-3" />
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest">
              Legal
            </p>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white mb-4 font-['Barlow_Condensed']">
            Terms of <span className="text-[#F5C518]">Service</span>
          </h1>
          <p className="text-zinc-500 text-sm">
            Last updated: {currentDate}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert max-w-none">
            
            {/* Agreement */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Agreement to Terms
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                These Terms of Service ("Terms") govern your access to and use of the Surfside Patrol website and security services. By accessing our website or engaging our services, you agree to be bound by these Terms.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                If you do not agree with any part of these Terms, you may not access the website or use our services. We reserve the right to modify these Terms at any time, and such modifications will be effective immediately upon posting.
              </p>
            </div>

            {/* Services Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Services Description
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Surfside Patrol provides professional private security services including, but not limited to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Executive and personal protection</li>
                <li>K9 security and property guarding</li>
                <li>Drone surveillance operations</li>
                <li>Armed and unarmed security officers</li>
                <li>Mobile patrol services</li>
                <li>CCTV installation and monitoring</li>
                <li>24-hour dispatch services</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                All services are provided in accordance with California state law and regulations governing private security companies. Surfside Patrol operates under Private Patrol Operator License PPO #15982.
              </p>
            </div>

            {/* Service Agreements */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Service Agreements
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Specific security services are provided under separate written service agreements that detail:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Scope of services to be provided</li>
                <li>Duration of service</li>
                <li>Pricing and payment terms</li>
                <li>Specific responsibilities of both parties</li>
                <li>Insurance and liability provisions</li>
                <li>Termination conditions</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                These Terms of Service apply in addition to any specific service agreement. In the event of a conflict, the specific service agreement shall prevail.
              </p>
            </div>

            {/* Client Responsibilities */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Client Responsibilities
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                As a client of Surfside Patrol, you agree to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Provide accurate and complete information about your security needs</li>
                <li>Grant necessary access to properties and facilities as required for services</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Pay for services in accordance with agreed-upon terms</li>
                <li>Notify us promptly of any security incidents or concerns</li>
                <li>Not interfere with our security personnel in the performance of their duties</li>
                <li>Maintain confidentiality of security protocols and procedures</li>
              </ul>
            </div>

            {/* Licensing and Insurance */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Licensing & Insurance
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Surfside Patrol is fully licensed and insured:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li><strong className="text-white">California Private Patrol Operator License:</strong> PPO #15982</li>
                <li><strong className="text-white">General Liability Insurance:</strong> Coverage as required by California law</li>
                <li><strong className="text-white">Workers' Compensation:</strong> Full coverage for all employees</li>
                <li><strong className="text-white">Bonded:</strong> As required by California Bureau of Security and Investigative Services</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Proof of licensing and insurance is available upon request.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Limitation of Liability
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Surfside Patrol shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability shall not exceed the amount paid for services in the twelve (12) months preceding the claim</li>
                <li>We are not responsible for losses caused by circumstances beyond our reasonable control</li>
                <li>Security services reduce but do not eliminate risk; we do not guarantee prevention of all security incidents</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Some jurisdictions do not allow certain limitations of liability, so some of the above limitations may not apply to you.
              </p>
            </div>

            {/* Indemnification */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Indemnification
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Surfside Patrol, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with your breach of these Terms or your use of our services in violation of applicable law.
              </p>
            </div>

            {/* Website Use */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Website Use
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                When using our website, you agree not to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Use the website for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to any portion of the website</li>
                <li>Interfere with or disrupt the website or servers</li>
                <li>Transmit any viruses, malware, or harmful code</li>
                <li>Collect or harvest any information from the website</li>
                <li>Impersonate any person or entity</li>
                <li>Use the website to send unsolicited communications</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Intellectual Property
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                All content on this website, including text, graphics, logos, images, and software, is the property of Surfside Patrol or its content suppliers and is protected by United States and international copyright laws. The compilation of all content on this site is the exclusive property of Surfside Patrol. You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
              </p>
            </div>

            {/* Confidentiality */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Confidentiality
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                Both parties agree to maintain the confidentiality of any proprietary or sensitive information disclosed during the course of our business relationship. This includes security protocols, client information, operational procedures, and any other information designated as confidential. This obligation survives termination of services.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Termination
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Either party may terminate services as specified in the applicable service agreement. In the absence of a specific agreement:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 ml-4">
                <li>Either party may terminate with 30 days written notice</li>
                <li>We may terminate immediately for non-payment or breach of terms</li>
                <li>Client remains responsible for payment for services rendered through termination date</li>
              </ul>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Governing Law
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the state or federal courts located in Orange County, California, and you consent to the jurisdiction of such courts.
              </p>
            </div>

            {/* Severability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Severability
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
              </p>
            </div>

            {/* Contact Us */}
            <div className="bg-[#121212] border border-zinc-800 p-8 mt-12">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
                Contact Us
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-center text-zinc-400">
                  <Phone className="h-5 w-5 text-[#F5C518] mr-3" />
                  <a href="tel:714-588-2460" className="hover:text-[#F5C518] transition-colors">714-588-2460</a>
                </div>
                <div className="flex items-center text-zinc-400">
                  <Mail className="h-5 w-5 text-[#F5C518] mr-3" />
                  <a href="mailto:surfside.josh4995@gmail.com" className="hover:text-[#F5C518] transition-colors">surfside.josh4995@gmail.com</a>
                </div>
                <div className="flex items-start text-zinc-400">
                  <MapPin className="h-5 w-5 text-[#F5C518] mr-3 mt-0.5" />
                  <span>2102 Business Center Dr<br />Irvine, CA 92612</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="text-[#F5C518] font-bold">Surfside Patrol Private Security</p>
                <p className="text-zinc-500 text-sm">PPO #15982</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
